import { useState, lazy, Suspense } from "react";
import { ArrowRight, Sparkles, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import heroBgXs from "@/assets/hero-bg.jpg?w=480&format=webp&quality=60";
import heroBgSm from "@/assets/hero-bg.jpg?w=640&format=webp&quality=70";
import heroBgMd from "@/assets/hero-bg.jpg?w=1024&format=webp&quality=75";
import heroBgLg from "@/assets/hero-bg.jpg?w=1600&format=webp&quality=80";
const AIChatDialog = lazy(() => import("@/components/AIChatDialog"));
import { useSiteSection } from "@/hooks/useSiteContent";

const HeroSection = () => {
  const [chatOpen, setChatOpen] = useState(false);
  const { data: hero } = useSiteSection("hero");

  const scrollTo = (href: string) => {
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  const badge = hero?.badge || "Schule Nr. 1 nach Wahl der Eltern";
  const titleBefore = hero?.title_before || "Entdecken Sie das";
  const titleHighlight = hero?.title_highlight || "Genie";
  const titleAfter = hero?.title_after || "in Ihrem Kind";
  const description = hero?.description || "Freizeitaktivität für Kinder, die eine gesunde Gehirnentwicklung fördert. Programme für Mentale Arithmetik und Numicon für Kinder von 3 bis 12 Jahren in Wien und Schwechat.";
  const ctaPrimary = hero?.cta_primary || "Kostenlose Probestunde";
  const ctaSecondary = hero?.cta_secondary || "Unsere Programme";
  const aiTitle = hero?.ai_search_title || "Informationssuche über die Schule";
  const aiSubtitle = hero?.ai_search_subtitle || "Fragen Sie uns alles über unsere Programme...";

  return (
    <>
      <section id="hero" className="relative min-h-[92svh] md:min-h-[90vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={heroBgXs}
            srcSet={`${heroBgXs} 480w, ${heroBgSm} 640w, ${heroBgMd} 1024w, ${heroBgLg} 1600w`}
            sizes="100vw"
            alt="Kinder lernen in der modernen Kleines Genie Schule in Österreich"
            width={1920}
            height={1080}
            className="w-full h-full object-cover"
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-b md:bg-gradient-to-r from-foreground/85 via-foreground/70 to-foreground/40 md:from-foreground/80 md:via-foreground/60 md:to-foreground/30" />
        </div>

        <div className="absolute top-20 right-10 w-20 h-20 rounded-full bg-secondary/20 hidden md:block" />

        <div className="container relative z-10 py-10 md:py-32">
          <div className="max-w-2xl animate-[fadeSlideUp_0.7s_ease-out_both]">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 rounded-full bg-primary/20 backdrop-blur-sm mb-4 md:mb-6">
              <Sparkles className="w-3.5 h-3.5 md:w-4 md:h-4 text-secondary" />
              <span className="text-xs md:text-sm font-body font-semibold text-primary-foreground/90">{badge}</span>
            </div>

            <h1 className="text-[2rem] leading-[1.15] sm:text-4xl md:text-5xl lg:text-6xl font-heading font-900 text-primary-foreground md:leading-tight mb-3 md:mb-6">
              {titleBefore}{" "}
              <span className="text-secondary">{titleHighlight}</span>{" "}
              {titleAfter}
            </h1>

            <p className="text-[0.95rem] md:text-xl text-primary-foreground/80 font-body mb-5 md:mb-8 max-w-xl leading-relaxed">
              {description}
            </p>

            <button
              onClick={() => setChatOpen(true)}
              className="w-full max-w-xl flex items-center gap-3 md:gap-4 px-4 py-3 md:px-6 md:py-4 mb-5 md:mb-8 rounded-2xl bg-card/20 backdrop-blur-md border-2 border-secondary/50 hover:border-secondary hover:bg-card/30 transition-all cursor-pointer group shadow-lg animate-[fadeSlideUp_0.7s_ease-out_0.3s_both]"
            >
              <div className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-xl bg-secondary/20 group-hover:bg-secondary/30 transition-colors flex-shrink-0">
                <Search className="w-5 h-5 md:w-6 md:h-6 text-secondary" />
              </div>
              <div className="text-left flex-1 min-w-0">
                <p className="text-primary-foreground font-body font-bold text-sm md:text-lg leading-snug">{aiTitle}</p>
                <p className="text-primary-foreground/60 font-body text-xs md:text-sm truncate">{aiSubtitle}</p>
              </div>
              <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-secondary/20 text-secondary text-xs font-body font-bold">
                <Sparkles className="w-3.5 h-3.5" />
                AI
              </div>
            </button>

            <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
              <Button size="lg" onClick={() => scrollTo("#kontakt")} className="bg-secondary text-primary-foreground font-body font-bold text-base md:text-lg px-6 md:px-8 py-5 md:py-6 shadow-card hover:shadow-card-hover transition-all hover:scale-105 outline outline-[0.5pt] outline-white shadow-[0_0_20px_rgba(255,255,255,0.3)] [text-shadow:_0_1px_2px_rgba(0,0,0,0.3)]">
                {ctaPrimary}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => scrollTo("#programi")} className="border-secondary text-primary-foreground bg-secondary/20 font-body font-bold text-base md:text-lg px-6 md:px-8 py-5 md:py-6 hover:bg-secondary/40 backdrop-blur-sm">
                {ctaSecondary}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {chatOpen && (
        <Suspense fallback={null}>
          <AIChatDialog open={chatOpen} onClose={() => setChatOpen(false)} />
        </Suspense>
      )}
    </>
  );
};

export default HeroSection;
